import { useTranslation } from "react-i18next";
import { Table } from "flowbite-react";
import { HiExclamation } from "react-icons/hi";

import { HsModelFieldPicker } from "./HsModelFieldPicker";
import { isFieldMapped } from "./mappingUtils";
import { useHubspot } from "../../../../contexts";

// Step 2 of the setup: map each Panora property to a HubSpot property (or
// mark it "Not used" inside the picker). Save/enable actions live in the
// stepper footer.
export const HsFieldsMappingTable = () => {
  const { panoraFields, localMapping, setLocalMapping, hsFields, pipelines } =
    useHubspot();
  const { t } = useTranslation();

  if (Object.keys(panoraFields).length === 0) {
    return (
      <div className="my-4 flex flex-col items-center justify-center gap-4">
        <HiExclamation className="h-12 w-12 text-yellow-500" />
        <p className="text-sm text-gray-500">
          {t("pages.settings.hubspot.noFieldsToMap")}
        </p>
      </div>
    );
  }

  const setFieldMapping = (fieldName, next) => {
    setLocalMapping((prev) => {
      if (next === undefined) {
        const { [fieldName]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [fieldName]: next };
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-gray-600">
        Map each Panora property to the HubSpot property that holds it — or pick
        “Not used” for properties you don't track.
      </p>

      {Object.entries(panoraFields).map(([category, fields]) => {
        const categoryMapped = fields.filter((field) =>
          isFieldMapped(localMapping[field.field_name]),
        ).length;

        return (
          <div
            key={category}
            className="flex w-full flex-col rounded-lg border border-gray-200 bg-white pt-4"
          >
            <div className="flex items-center justify-between px-6 pb-4">
              <h4 className="text-lg font-semibold capitalize text-black">
                {category} Fields
              </h4>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  categoryMapped === fields.length
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {categoryMapped}/{fields.length} mapped
              </span>
            </div>
            <Table className="w-full drop-shadow-none">
              <Table.Head>
                <Table.HeadCell className="text-left">
                  Panora Property
                </Table.HeadCell>
                <Table.HeadCell className="text-center">
                  Hubspot Property
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {fields.map(
                  ({
                    field_name,
                    display_text,
                    display_description,
                    requires_pipeline = false,
                  }) => (
                    <Table.Row key={field_name}>
                      <Table.Cell className="w-1/2">
                        <p className="text-s font-semibold text-black">
                          {display_text}
                        </p>
                        <p className="text-xs text-gray-500">
                          {display_description}
                        </p>
                      </Table.Cell>
                      <Table.Cell className="flex justify-center">
                        <HsModelFieldPicker
                          fieldsMap={hsFields}
                          pipelines={pipelines}
                          mapping={localMapping[field_name]}
                          onChange={(next) => setFieldMapping(field_name, next)}
                          pipelineRequired={requires_pipeline}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ),
                )}
              </Table.Body>
            </Table>
          </div>
        );
      })}
    </div>
  );
};
